import { ReactNode } from 'react';

import { RemixiconComponentType } from '@remixicon/react';

import {
  ItemRenderExtensionOptions,
  createItemRenderExtension,
  createItemRenderExtensions,
  useItemRenderExtensions,
} from '@spotify/confidence-core-react';
import { ConversationsV2ToolConfirmationResponseDecision } from '@spotify/confidence-graphql';

export type ToolGroupEntry = {
  tool: string;
  eventName?: string;
  displayName?: string | null;
  startedAt?: number;
  isStreaming?: boolean;
  input?: unknown;
  result?: { content: string; success: boolean };
};

export type PreviewField = {
  key: string;
  label?: string;
};

export type ToolRenderOptions = {
  isStreaming: boolean;
};

export type ToolIconRenderOptions = ToolRenderOptions & {
  size: number;
  className?: string;
};

export interface ToolConfirmationRenderProps {
  input: unknown;
  onDecide: (decision: ConversationsV2ToolConfirmationResponseDecision) => void;
  actionsDisabled: boolean;
  pendingDecision: ConversationsV2ToolConfirmationResponseDecision | null;
  error?: Error | null;
}

export interface ToolExtensionOptions
  extends ItemRenderExtensionOptions<string> {
  icon?: RemixiconComponentType;
  renderIcon?: (
    entry: ToolGroupEntry,
    options: ToolIconRenderOptions,
  ) => ReactNode;
  display?:
    | 'prominent'
    | 'grouped'
    | ((entry: ToolGroupEntry) => 'prominent' | 'grouped');
  collapseConsecutiveCalls?: boolean;
  formatLabel?: (entry: ToolGroupEntry) => { text: ReactNode; detail?: string };
  renderLabel?: (
    entry: ToolGroupEntry,
    options: ToolRenderOptions,
  ) => ReactNode;
  renderTool?: (entry: ToolGroupEntry, options: ToolRenderOptions) => ReactNode;
  previewFields?: PreviewField[];
  confirmationTitle?: string;
  renderConfirmation?: (props: ToolConfirmationRenderProps) => ReactNode;
}

export const isProminentToolEntry = (
  extension: ToolExtensionOptions | undefined,
  entry: ToolGroupEntry,
): boolean =>
  (typeof extension?.display === 'function'
    ? extension.display(entry)
    : extension?.display) === 'prominent';

export const ToolExtensions = createItemRenderExtensions<ToolExtensionOptions>({
  id: 'tool',
});

export const createToolExtension = (options: ToolExtensionOptions) => {
  return createItemRenderExtension<ToolExtensionOptions>(options);
};

export const mcpTool = (name: string) => (tool: string) =>
  tool === name || tool.endsWith(`__${name}`);

const PULSE_MUTATION_TOOL_NAMES = [
  'save_to_pulse',
  'create_pulse',
  'append_to_pulse',
] as const;

export const isPulseMutationTool = (tool: string): boolean =>
  PULSE_MUTATION_TOOL_NAMES.some(name => mcpTool(name)(tool));

export const isCreatePulseTool = (tool: string): boolean =>
  mcpTool('create_pulse')(tool);

export const useToolExtensions = () => {
  return useItemRenderExtensions<ToolExtensionOptions>(ToolExtensions);
};
